import {
  Outlet,
  createRootRoute,
  useRouter,
  useRouterState
} from '@tanstack/react-router'
import { useEffect, useRef, useState, type JSX } from 'react'
import { Toaster } from 'sonner'

import { ThemeToggle } from '../components/theme-toggle.tsx'
import { Button } from '../design-system/button.tsx'
import { Icon } from '../design-system/icon.tsx'
import { Link } from '../design-system/link.tsx'
import { NavSheet } from '../design-system/nav-sheet.tsx'
import { Notice } from '../design-system/notice.tsx'
import {
  groupInternalPages,
  normalizePath,
  type InternalPage
} from '../lib/pages.ts'
import { INTERNAL_PAGES } from './-nav.ts'
import { cn } from '../lib/cn.ts'
import { IDLE_WARNING_EVENT } from '../lib/idle.ts'
import { useSession } from '../session.tsx'

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: function RouterError({ error }) {
    return (
      <div className="p-8">
        <Notice tone="danger" data-testid="router-error">
          {error.message}
        </Notice>
      </div>
    )
  }
})

const SIDEBAR_CONTROL_CLASS =
  'text-sidebar-foreground hover:bg-sidebar-accent/40 hover:text-sidebar-foreground focus-visible:ring-sidebar-ring'

function rowClass(active: boolean): string {
  return cn(
    'text-sidebar-foreground/80 flex items-center gap-3 rounded-md px-3 py-2 text-sm',
    'hover:bg-sidebar-accent/40 focus-visible:ring-sidebar-ring cursor-pointer',
    'text-left focus-visible:ring-2 focus-visible:outline-none',
    active && 'bg-sidebar-accent/60 text-sidebar-accent-foreground font-medium'
  )
}

/** A group name as a `data-testid` fragment: lower-cased, spaces to hyphens. "CS Tools" becomes "cs-tools". */
function slugifyGroup(group: string): string {
  return group.trim().toLowerCase().replace(/\s+/g, '-')
}

/** Every page in this console renders the same row; a group header and a flat entry both build one from it. */
function NavRow({
  page,
  active,
  testId,
  onNavigate
}: {
  page: InternalPage
  active: boolean
  testId: string
  onNavigate: () => void
}): JSX.Element {
  return (
    <Link
      data-testid={testId}
      href={page.path}
      onNavigate={onNavigate}
      className={rowClass(active)}
      aria-current={active ? 'page' : undefined}
    >
      <Icon name="app-window" className="size-4 shrink-0" />
      {page.title}
    </Link>
  )
}

/** Navigation uses the declared page list in both desktop and mobile layouts. */
function AppNav({
  pathname,
  mobile = false,
  onSelect
}: {
  pathname: string
  mobile?: boolean
  onSelect: () => void
}): JSX.Element {
  const router = useRouter()
  const { userName, onSignOut } = useSession()
  const prefix = mobile ? 'mobile-' : ''
  // The declared page list and nothing else. There is no Home row, because
  // there is no home page — `/` redirects to the first app, so a Home row
  // would lead back to whichever row sits below it.
  const { ungrouped, sections } = groupInternalPages(INTERNAL_PAGES)
  // Every section opens by default. A reader who has never touched the rail
  // should still see every destination it holds; collapsing is something
  // they choose, not something the rail starts them with.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(sections.map(section => [section.group, true]))
  )

  function navigate(path: string): void {
    onSelect()
    router.history.push(path)
  }

  return (
    <nav aria-label="Internal apps" className="flex flex-col gap-1 p-3">
      <p
        data-testid={`${prefix}app-title`}
        className="text-sidebar-foreground px-3 pt-2 text-lg font-semibold"
      >
        BlytzPay Internal
      </p>
      <p
        data-testid={`${prefix}session-user`}
        className="text-sidebar-foreground/70 truncate px-3 pt-2 pb-4 text-sm"
      >
        {userName}
      </p>
      {ungrouped.map(page => (
        <NavRow
          key={page.id}
          page={page}
          active={normalizePath(pathname) === normalizePath(page.path)}
          testId={`${prefix}nav-${page.id}`}
          onNavigate={() => navigate(page.path)}
        />
      ))}
      {sections.map(section => {
        const slug = slugifyGroup(section.group)
        const open = openGroups[section.group] ?? true

        return (
          <div key={section.group} className="flex flex-col gap-1">
            <button
              type="button"
              data-testid={`${prefix}nav-group-${slug}`}
              aria-expanded={open}
              aria-controls={`${prefix}nav-group-${slug}-items`}
              className={cn(rowClass(false), 'justify-between')}
              onClick={() =>
                setOpenGroups(current => ({
                  ...current,
                  [section.group]: !open
                }))
              }
            >
              {section.group}
              <Icon
                name={open ? 'chevron-up' : 'chevron-down'}
                className="size-4 shrink-0"
              />
            </button>
            {open ? (
              <div
                id={`${prefix}nav-group-${slug}-items`}
                data-testid={`${prefix}nav-group-${slug}-items`}
                className="flex flex-col gap-1 pl-4"
              >
                {section.pages.map(page => (
                  <NavRow
                    key={page.id}
                    page={page}
                    active={
                      normalizePath(pathname) === normalizePath(page.path)
                    }
                    testId={`${prefix}nav-${page.id}`}
                    onNavigate={() => navigate(page.path)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
      <div className="bg-sidebar-border my-2 h-px" />
      <button
        type="button"
        data-testid={`${prefix}session-sign-out`}
        className={rowClass(false)}
        onClick={() => {
          onSelect()
          onSignOut()
        }}
      >
        <Icon name="log-out" className="size-4 shrink-0" />
        Sign out
      </button>
    </nav>
  )
}

/** The shell follows super's desktop rail and mobile navigation sheet. */
function RootLayout(): JSX.Element {
  const { userName, error } = useSession()
  const pathname = useRouterState({ select: state => state.location.pathname })
  const [menuOpen, setMenuOpen] = useState(false)
  const menuTrigger = useRef<HTMLButtonElement>(null)
  const content = useRef<HTMLElement>(null)

  useEffect(() => {
    const close = (): void => setMenuOpen(false)
    window.addEventListener(IDLE_WARNING_EVENT, close)
    return () => window.removeEventListener(IDLE_WARNING_EVENT, close)
  }, [])

  function closeMenu(): void {
    setMenuOpen(false)
  }

  return (
    <div
      data-testid="app-root"
      className="bg-background text-foreground min-h-svh md:grid md:grid-cols-rail"
    >
      <aside
        data-testid="console-sidebar"
        className="bg-sidebar hidden md:flex md:flex-col"
      >
        <AppNav pathname={pathname} onSelect={closeMenu} />
        <div className="mt-auto p-3">
          <ThemeToggle
            data-testid="session-theme"
            className={SIDEBAR_CONTROL_CLASS}
          />
        </div>
      </aside>
      <div className="flex min-w-0 flex-col">
        <header className="bg-sidebar flex items-center gap-3 px-3 py-2 md:hidden">
          <Button
            data-testid="console-nav-toggle"
            ref={menuTrigger}
            variant="quiet"
            size="icon"
            aria-label="Menu"
            aria-expanded={menuOpen}
            className={SIDEBAR_CONTROL_CLASS}
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" />
          </Button>
          <span className="text-sidebar-foreground/70 truncate text-sm">
            {userName}
          </span>
        </header>
        <NavSheet
          data-testid="console-nav"
          open={menuOpen}
          onOpenChange={setMenuOpen}
          title="Internal navigation"
          onCloseAutoFocus={event => {
            event.preventDefault()
            const target = menuTrigger.current?.getClientRects().length
              ? menuTrigger.current
              : content.current
            target?.focus()
          }}
        >
          <AppNav pathname={pathname} mobile onSelect={closeMenu} />
          <div className="p-3">
            <ThemeToggle
              data-testid="mobile-session-theme"
              className={SIDEBAR_CONTROL_CLASS}
            />
          </div>
        </NavSheet>
        <main
          ref={content}
          tabIndex={-1}
          data-testid="console-content"
          className="flex min-w-0 flex-col gap-6 p-6 md:p-8"
        >
          {error ? (
            <Notice tone="danger" data-testid="session-error">
              {error}
            </Notice>
          ) : null}
          <Outlet />
        </main>
        <Toaster />
      </div>
    </div>
  )
}
